/**
 * Mid-app dApp sign UI + play-session keep-alive.
 * Phantom Connect signs; play ledger sits. No signer → /connect.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useWalletState } from "@/contexts/wallet-context";
import {
  signMessageBase58,
  type ConnectedSolanaWallet,
} from "@/lib/solana-wallets";
import {
  fetchActionChallenge,
  purposeBlurb,
  purposeTitle,
  type SignPurpose,
} from "@/lib/dapp-sign";
import type { PlayAction } from "@/lib/signed-action";
import { signedBodyFields, type SignedChallenge } from "@/lib/signed-action";
import {
  clearPlaySession,
  playSessionHeaders,
  readPlaySession,
  writePlaySession,
} from "@/lib/play-session";
import {
  PLAY_SESSION_DEFAULT_HOURS,
  warmPlaySession,
} from "@/lib/play-ledger-session";
import { DappSignModal } from "@/components/dapp-sign-modal";

export type SignDetail = { label: string; value: string };

export type SignRequestInput = {
  purpose: SignPurpose;
  /** Pre-built message (P2P) — skips challenge fetch */
  message?: string;
  /** Build challenge for this action */
  action?: PlayAction;
  params?: Record<string, string | number | undefined>;
  details?: SignDetail[];
  title?: string;
  description?: string;
};

export type SignRequestResult = {
  message: string;
  signature: string;
  wallet: ConnectedSolanaWallet;
  nonce?: string;
};

type Pending = SignRequestInput & {
  resolve: (r: SignRequestResult) => void;
  reject: (e: Error) => void;
  message: string;
  nonce?: string;
  phase: "ready" | "signing" | "error";
  error?: string;
};

type DappSignContextType = {
  /** Open centered Sign UI, then wallet.signMessage */
  requestSign: (req: SignRequestInput) => Promise<SignRequestResult>;
  /**
   * Ensure long play session (default 24h) for join/spin/BJ.
   * Silent if local+server session still valid — no Web3 popup.
   * Opens Sign UI only when missing/expired.
   */
  ensurePlaySession: (opts?: { silentOnly?: boolean }) => Promise<string | null>;
  /** Re-validate stored token with edge (no sign). */
  refreshPlaySessionStatus: () => Promise<void>;
  playSessionActive: boolean;
  playSessionExp: number | null;
};

const DappSignContext = createContext<DappSignContextType | null>(null);

export function useDappSign() {
  const ctx = useContext(DappSignContext);
  if (!ctx) {
    throw new Error("useDappSign must be used within DappSignProvider");
  }
  return ctx;
}

export function DappSignProvider({ children }: { children: ReactNode }) {
  const {
    wallet,
    ensureSigner,
    getConnectedWallet,
    canSign,
    openWalletPanel,
  } = useWalletState();
  const [pending, setPending] = useState<Pending | null>(null);
  const [playSessionExp, setPlaySessionExp] = useState<number | null>(() => {
    const s = readPlaySession(wallet.publicKey);
    return s?.exp ?? null;
  });
  const keepaliveRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Sync session exp when wallet changes + warm with edge
  useEffect(() => {
    const s = readPlaySession(wallet.publicKey);
    if (wallet.publicKey && s && s.wallet !== wallet.publicKey) {
      clearPlaySession();
      setPlaySessionExp(null);
      return;
    }
    setPlaySessionExp(s?.exp ?? null);
    if (wallet.publicKey) {
      void warmPlaySession(wallet.publicKey).then((st) => {
        setPlaySessionExp(st.active ? st.exp : null);
      });
    }
  }, [wallet.publicKey]);

  // Silent rebind — wallet + session across pages/games
  useEffect(() => {
    const rebind = () => {
      if (!wallet.publicKey) return;
      void ensureSigner({ silentOnly: true });
      void warmPlaySession(wallet.publicKey).then((st) => {
        setPlaySessionExp(st.active ? st.exp : null);
      });
    };
    rebind();
    const onVis = () => {
      if (document.visibilityState === "visible") rebind();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", rebind);
    // 3 min silent keep-alive (token is 24h)
    keepaliveRef.current = setInterval(rebind, 180_000);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", rebind);
      if (keepaliveRef.current) clearInterval(keepaliveRef.current);
    };
  }, [wallet.publicKey, wallet.walletId, ensureSigner]);

  const refreshPlaySessionStatus = useCallback(async () => {
    const st = await warmPlaySession(wallet.publicKey);
    setPlaySessionExp(st.active ? st.exp : null);
  }, [wallet.publicKey]);

  const requestSign = useCallback(
    async (req: SignRequestInput): Promise<SignRequestResult> => {
      const pk = wallet.publicKey;
      if (!pk || pk.length < 32) {
        throw new Error("Connect a wallet first");
      }

      let message = req.message?.trim() || "";
      let nonce: string | undefined;
      if (!message) {
        const action = req.action || (req.purpose as PlayAction);
        if (
          !action ||
          ![
            "play_session",
            "table_join",
            "slots_spin",
            "cashout",
            "pro_claim",
          ].includes(action)
        ) {
          throw new Error("message or action required to sign");
        }
        const ch = await fetchActionChallenge({
          wallet: pk,
          action: action as PlayAction,
          params: req.params,
        });
        message = ch.message;
        nonce = ch.nonce;
      }

      return new Promise<SignRequestResult>((resolve, reject) => {
        setPending({
          ...req,
          message,
          nonce,
          resolve,
          reject,
          phase: "ready",
        });
      });
    },
    [wallet.publicKey],
  );

  const cancelPending = useCallback(() => {
    setPending((p) => {
      if (p) p.reject(new Error("Signature cancelled"));
      return null;
    });
  }, []);

  const confirmSign = useCallback(async () => {
    if (!pending) return;
    setPending((p) => (p ? { ...p, phase: "signing", error: undefined } : p));
    try {
      let connected = getConnectedWallet();
      if (!connected) {
        connected = await ensureSigner({ silentOnly: false });
      }
      if (!connected) {
        const custodial = wallet.custodialWallet;
        const isCustodialPlay =
          custodial &&
          wallet.publicKey &&
          custodial === wallet.publicKey &&
          !canSign;
        throw new Error(
          isCustodialPlay
            ? "Connect Phantom to sign. Extension, or Google / Apple on /connect."
            : "Reconnect Phantom, Solflare, or another inject wallet to sign",
        );
      }
      if (
        wallet.publicKey &&
        connected.publicKey !== wallet.publicKey
      ) {
        throw new Error(
          `Signing wallet ${connected.publicKey.slice(0, 4)}… does not match active play address`,
        );
      }
      const signature = await signMessageBase58(
        connected,
        pending.message,
      );
      const result: SignRequestResult = {
        message: pending.message,
        signature,
        wallet: connected,
        nonce: pending.nonce,
      };
      pending.resolve(result);
      setPending(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setPending((p) =>
        p ? { ...p, phase: "error", error: msg } : p,
      );
    }
  }, [
    pending,
    getConnectedWallet,
    ensureSigner,
    wallet.publicKey,
    wallet.custodialWallet,
    canSign,
  ]);

  const ensurePlaySession = useCallback(
    async (opts?: { silentOnly?: boolean }): Promise<string | null> => {
      const pk = wallet.publicKey;
      if (!pk || pk.length < 32) {
        throw new Error("Connect a wallet first");
      }

      // 1) Local token + server warm — no popup
      const warmed = await warmPlaySession(pk);
      if (warmed.active && warmed.exp) {
        const local = readPlaySession(pk);
        if (local?.token) {
          setPlaySessionExp(warmed.exp);
          return local.token;
        }
      }

      if (opts?.silentOnly) {
        setPlaySessionExp(null);
        return null;
      }

      // 2) One sign for 24h — covers all games/pages
      const signed = await requestSign({
        purpose: "play_session",
        action: "play_session",
        title: "Authorize play (24 hours)",
        description: purposeBlurb("play_session"),
        details: [
          { label: "Wallet", value: `${pk.slice(0, 4)}…${pk.slice(-4)}` },
          {
            label: "Session",
            value: `${PLAY_SESSION_DEFAULT_HOURS} hours`,
          },
          { label: "Covers", value: "Tables · Slots · Blackjack" },
          { label: "No re-sign", value: "Navigate freely until expiry" },
        ],
      });

      const r = await fetch("/api/play/session", {
        method: "POST",
        headers: playSessionHeaders(pk, {
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({
          wallet: pk,
          ...signedBodyFields({
            message: signed.message,
            signature: signed.signature,
            nonce: signed.nonce || "",
            action: "play_session",
          } as SignedChallenge),
        }),
      });
      const j = (await r.json()) as {
        token?: string;
        exp?: number;
        ttlHours?: number;
        error?: string;
      };
      if (!r.ok || !j.token) {
        throw new Error(j.error || "Could not open play session");
      }
      const exp =
        j.exp ||
        Date.now() +
          (j.ttlHours || PLAY_SESSION_DEFAULT_HOURS) * 3_600_000;
      writePlaySession({ wallet: pk, token: j.token, exp });
      setPlaySessionExp(exp);
      return j.token;
    },
    [wallet.publicKey, requestSign],
  );

  const value: DappSignContextType = {
    requestSign,
    ensurePlaySession,
    refreshPlaySessionStatus,
    playSessionActive: Boolean(
      wallet.publicKey &&
        playSessionExp &&
        playSessionExp > Date.now() + 30_000,
    ),
    playSessionExp,
  };

  const walletLabel =
    wallet.walletType ||
    (canSign ? "Solana wallet" : wallet.custodialWallet ? "Crossmint" : "Wallet");

  return (
    <DappSignContext.Provider value={value}>
      {children}
      <DappSignModal
        open={Boolean(pending)}
        title={
          pending?.title ||
          purposeTitle(pending?.purpose || "custom")
        }
        description={
          pending?.description ||
          purposeBlurb(pending?.purpose || "custom")
        }
        details={pending?.details || []}
        walletLabel={walletLabel}
        walletAddress={wallet.publicKey}
        canSign={canSign || Boolean(getConnectedWallet())}
        isCustodialOnly={Boolean(
          wallet.custodialWallet &&
            wallet.publicKey === wallet.custodialWallet &&
            !canSign &&
            !getConnectedWallet(),
        )}
        phase={pending?.phase || "ready"}
        error={pending?.error}
        messagePreview={pending?.message}
        onConfirm={() => void confirmSign()}
        onCancel={cancelPending}
        onConnect={() => {
          openWalletPanel("connect");
        }}
        onOpenGrudgeWallet={() => {
          window.location.href = "/connect";
        }}
      />
    </DappSignContext.Provider>
  );
}
